export type Operator =
  | "eq"
  | "neq"
  | "contains"
  | "notContains"
  | "startsWith"
  | "endsWith"
  | "between";

export function compareValues(
  a: unknown,
  b: unknown,
  operator: Operator,
  { caseSensitive }: { caseSensitive?: boolean } = {
    caseSensitive: false,
  },
) {
  if (operator === "eq") {
    // First try with simple equality
    if (caseSensitive ? a == b : asLowerCase(a) == asLowerCase(b)) {
      return true;
    }

    // If b is an array, check if a is in the array
    if (b instanceof Array) {
      return b.some(
        (item) =>
          (caseSensitive ? asLowerCase(item) : item) ==
          (caseSensitive ? asLowerCase(a) : a),
      );
    }
  } else if (operator === "neq") {
    return !compareValues(a, b, "eq", { caseSensitive });
  } else if (operator === "contains") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString.includes(bAsString);
    }

    if (a instanceof Array && b instanceof Array) {
      return a.some((item) => b.includes(item));
    }
  } else if (operator === "notContains") {
    return !compareValues(a, b, "contains", { caseSensitive });
  } else if (operator === "startsWith") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString.startsWith(bAsString);
    }
  } else if (operator === "endsWith") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString.endsWith(bAsString);
    }
  } else if (operator === "between") {
    const [fromValue, toValue] = Array.isArray(b) && b.length === 2 ? b : [];
    return isBetween(a, fromValue, toValue, { caseSensitive });
  }
  return false;
}

function isBetween(
  value: unknown,
  fromValue: unknown,
  toValue: unknown,
  { caseSensitive }: { caseSensitive?: boolean } = {},
): boolean {
  if (value instanceof Date) {
    return isWithinDateRange(value, fromValue, toValue);
  }

  if (typeof value === "number") {
    return isWithinNumberRange(value, fromValue, toValue);
  }

  if (typeof value === "string") {
    const date = asDate(value);
    if (date) {
      return isWithinDateRange(date, fromValue, toValue);
    }
  }

  return isWithinStringRange(value, fromValue, toValue, { caseSensitive });
}

function asDate(value: unknown): Date | undefined {
  const date =
    value instanceof Date
      ? value
      : typeof value === "string" || typeof value === "number"
        ? new Date(value)
        : undefined;

  return date && !Number.isNaN(date.getTime()) ? date : undefined;
}

function isWithinDateRange(
  value: Date,
  fromValue: unknown,
  toValue: unknown,
): boolean {
  if (typeof fromValue === "string" && fromValue !== "") {
    const from = asDate(fromValue);
    if (from && value.getTime() < from.getTime()) {
      return false;
    }
  }

  if (typeof toValue === "string" && toValue !== "") {
    const to = asDate(toValue);
    if (to) {
      to.setHours(23, 59, 59, 999);
      if (value.getTime() > to.getTime()) {
        return false;
      }
    }
  }

  return true;
}

function isWithinNumberRange(
  value: number,
  fromValue: unknown,
  toValue: unknown,
): boolean {
  if (typeof fromValue === "string" && fromValue !== "") {
    const from = Number(fromValue);
    if (!Number.isNaN(from) && value < from) {
      return false;
    }
  }

  if (typeof toValue === "string" && toValue !== "") {
    const to = Number(toValue);
    if (!Number.isNaN(to) && value > to) {
      return false;
    }
  }

  return true;
}

function isWithinStringRange(
  value: unknown,
  fromValue: unknown,
  toValue: unknown,
  { caseSensitive }: { caseSensitive?: boolean } = {},
): boolean {
  const valueAsString = caseSensitive ? asString(value) : asString(asLowerCase(value));
  if (valueAsString === undefined) {
    return false;
  }

  if (typeof fromValue === "string" && fromValue !== "") {
    const from = caseSensitive ? fromValue : fromValue.toLowerCase();
    if (valueAsString < from) {
      return false;
    }
  }

  if (typeof toValue === "string" && toValue !== "") {
    const to = caseSensitive ? toValue : toValue.toLowerCase();
    if (valueAsString > to) {
      return false;
    }
  }

  return true;
}

function asLowerCase(value: unknown): unknown {
  if (typeof value === "string") {
    return value.toLowerCase();
  }
  return value;
}

function asString(value: unknown): string | undefined {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value.toString();
  } else if (
    typeof value === "object" &&
    value !== null &&
    value !== undefined &&
    "toString" in value &&
    typeof value.toString === "function"
  ) {
    const stringValue = (value as { toString: () => string }).toString();
    if (typeof stringValue === "string") {
      return stringValue;
    }
  }
}
